import { supabase } from "@/integrations/supabase/client";
import { getAnalyticsConsent } from "@/lib/consent";

// Debounce utility to prevent excessive calls
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Track recent events to prevent duplicates
const recentEvents = new Map<string, number>();
const DUPLICATE_THRESHOLD = 30000; // 30 seconds

// Analytics tracking utility functions
export const trackAnalyticsEvent = async (eventData: {
  posit_id: string;
  user_id?: string;
  event_type: 'question_asked' | 'subscription_created' | 'subscription_cancelled' | 'revenue_generated' | 'user_engagement';
  event_data?: Record<string, any>;
  revenue_amount?: number;
  session_id?: string;
}) => {
  try {
    // Respect analytics consent; skip if not granted
    const consent = getAnalyticsConsent();
    if (consent !== true) {
      return true;
    }
    // Create unique key for duplicate detection
    const eventKey = `${eventData.event_type}_${eventData.posit_id}_${eventData.user_id || 'anon'}`;
    const now = Date.now();
    
    // Check if we've tracked this recently
    if (recentEvents.has(eventKey)) {
      const lastTracked = recentEvents.get(eventKey)!;
      if (now - lastTracked < DUPLICATE_THRESHOLD) {
        return true;
      }
    }

    const { data, error } = await supabase.functions.invoke('track-analytics', {
      body: eventData
    });

    if (error) {
      // If function requires internal token, client calls may be unauthorized — ignore silently
      const message = String((error as any)?.message || error);
      if (message.toLowerCase().includes('unauthorized') || message.includes('401')) {
        return true;
      }
      return false;
    }

    // Mark as tracked
    recentEvents.set(eventKey, now);
    
    // Clean up old entries
    if (recentEvents.size > 100) {
      for (const [key, timestamp] of recentEvents.entries()) {
        if (now - timestamp > DUPLICATE_THRESHOLD) {
          recentEvents.delete(key);
        }
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};

// Convenience functions for common events
export const trackQuestionAsked = async (positId: string, userId?: string, sessionId?: string) => {
  return trackAnalyticsEvent({
    posit_id: positId,
    user_id: userId,
    event_type: 'question_asked',
    session_id: sessionId
  });
};

export const trackRevenue = async (positId: string, amount: number, userId?: string) => {
  return trackAnalyticsEvent({
    posit_id: positId,
    user_id: userId,
    event_type: 'revenue_generated',
    revenue_amount: amount
  });
};

export const trackUserEngagement = async (positId: string, userId?: string, engagementType?: string) => {
  return trackAnalyticsEvent({
    posit_id: positId,
    user_id: userId,
    event_type: 'user_engagement',
    event_data: { engagement_type: engagementType }
  });
};

// Optimized page view tracking with debouncing
const trackPageViewInternal = (pageName: string, properties: Record<string, any> = {}) => {
  try {
    // For now, just log the page view - could be enhanced with actual tracking
  } catch (error) {
  }
};

// Debounced version to prevent excessive calls during navigation
export const trackPageView = debounce(trackPageViewInternal, 500);

// Legacy trackEvent function with performance optimization
export const trackEvent = (eventName: string, properties: Record<string, any> = {}) => {
  try {
    // Only log in development to reduce noise
    if (process.env.NODE_ENV === 'development') {
    }
  } catch (error) {
  }
};

// Analytics event constants
export const ANALYTICS_EVENTS = {
  SIGN_UP: 'sign_up',
  SIGN_IN: 'sign_in',
  SEND_MESSAGE: 'send_message',
  SHARE_POSIT: 'share_posit',
  COMPLETE_TASK: 'complete_task',
  CREATE_POSIT: 'create_posit',
  UPDATE_POSIT: 'update_posit',
  VIEW_POSIT: 'view_posit',
  ERROR_OCCURRED: 'error_occurred',
} as const;